import { Pair as FewV2Pair, Route as FewV2Route } from '@ring-protocol/few-v2-sdk'
import { Currency, CurrencyAmount, Percent, TradeType } from '@uniswap/sdk-core'
import { ClassicQuoteResponse } from 'uniswap/src/data/apiClients/tradingApi/TradingApiClient'
import {
  ChainId,
  NullablePermit,
  Routing,
  TradeType as TradingApiTradeType,
} from 'uniswap/src/data/tradingApi/__generated__'
import { LOCAL_FEW_V2_FALLBACK_GAS_FEE_WEI } from 'uniswap/src/features/transactions/swap/steps/localFewV2Swap'
import { ClassicTrade, TradeWithStatus } from 'uniswap/src/features/transactions/swap/types/trade'

const DEFAULT_LOCAL_SLIPPAGE_PERCENT = 0.5

export function shouldSkipTradingApiQuote({
  amount,
  currencyInEqualsCurrencyOut,
  isFewTokenPair,
  isZeroAmount,
  requiredPoolAddress,
  skip,
  tokenInAddress,
  tokenInChainId,
  tokenOutAddress,
  tokenOutChainId,
}: {
  amount?: CurrencyAmount<Currency>
  currencyInEqualsCurrencyOut?: boolean
  isFewTokenPair: boolean
  isZeroAmount: boolean
  requiredPoolAddress?: string
  skip?: boolean
  tokenInAddress?: string
  tokenInChainId?: number
  tokenOutAddress?: string
  tokenOutChainId?: number
}): boolean {
  return Boolean(
    skip ||
      !tokenInAddress ||
      !tokenOutAddress ||
      !tokenInChainId ||
      !tokenOutChainId ||
      !amount ||
      isZeroAmount ||
      currencyInEqualsCurrencyOut ||
      isFewTokenPair ||
      requiredPoolAddress,
  )
}

export function getLocalFewV2TradeStatus({
  isDebouncing,
  localTrade,
  requiredPoolAddress,
  requiredPoolPairIsLoading,
}: {
  isDebouncing?: boolean
  localTrade?: ClassicTrade
  requiredPoolAddress?: string
  requiredPoolPairIsLoading?: boolean
}): TradeWithStatus | undefined {
  if (!requiredPoolAddress) {
    return undefined
  }

  if (localTrade) {
    return {
      isLoading: false,
      isFetching: false,
      trade: localTrade,
      indicativeTrade: undefined,
      isIndicativeLoading: false,
      error: null,
      gasEstimates: undefined,
    }
  }

  const isLoading = Boolean(requiredPoolPairIsLoading || isDebouncing)
  return {
    isLoading,
    isFetching: false,
    trade: null,
    indicativeTrade: undefined,
    isIndicativeLoading: false,
    error: isLoading ? null : new Error('The selected FewV2 pair could not be quoted from its onchain reserves.'),
    gasEstimates: undefined,
  }
}

function toRouteToken(currency: Currency): {
  address: string
  chainId: ChainId
  decimals: string
  symbol: string
} {
  const token = currency.wrapped
  return {
    address: token.address,
    chainId: token.chainId as ChainId,
    decimals: token.decimals.toString(),
    symbol: token.symbol ?? 'UNKNOWN',
  }
}

/**
 * Creates the narrow exact-input trade used by the FewToken maintenance flow
 * directly from the selected pair's onchain reserves. This keeps the locked-pair
 * tool usable when the Trading API is unavailable or rate limited.
 */
export function createLocalFewV2Trade({
  amountSpecified,
  deadline,
  otherCurrency,
  pair,
  pairAddress,
  permitData = null,
  slippageTolerance = DEFAULT_LOCAL_SLIPPAGE_PERCENT,
  swapper,
  tradeType,
}: {
  amountSpecified: CurrencyAmount<Currency>
  deadline: number
  otherCurrency: Currency
  pair: FewV2Pair
  pairAddress: string
  permitData?: NullablePermit
  slippageTolerance?: number
  swapper: string
  tradeType: TradeType
}): ClassicTrade | undefined {
  if (tradeType !== TradeType.EXACT_INPUT || amountSpecified.currency.chainId !== otherCurrency.chainId) {
    return undefined
  }

  try {
    const inputToken = amountSpecified.currency.wrapped
    const outputToken = otherCurrency.wrapped
    const inputAmount = CurrencyAmount.fromRawAmount(inputToken, amountSpecified.quotient)
    const [outputAmount] = pair.getOutputAmount(inputAmount)
    const route = new FewV2Route([pair], inputToken, outputToken)
    const slippageBps = Math.round(slippageTolerance * 100)
    const minimumOutputAmount = outputAmount.multiply(new Percent(10_000 - slippageBps, 10_000))
    const tokenIn = toRouteToken(inputToken)
    const tokenOut = toRouteToken(outputToken)
    const reserve0Token = toRouteToken(pair.reserve0.currency)
    const reserve1Token = toRouteToken(pair.reserve1.currency)
    const requestId = `local-fewv2-${inputToken.chainId}-${pairAddress.toLowerCase()}-${inputAmount.quotient.toString()}`

    const quote: ClassicQuoteResponse = {
      requestId,
      routing: Routing.CLASSIC,
      permitData,
      quote: {
        input: { token: inputToken.address, amount: inputAmount.quotient.toString() },
        output: { token: outputToken.address, amount: outputAmount.quotient.toString(), recipient: swapper },
        swapper,
        chainId: inputToken.chainId as ChainId,
        slippage: slippageTolerance,
        tradeType: TradingApiTradeType.EXACT_INPUT,
        gasFee: LOCAL_FEW_V2_FALLBACK_GAS_FEE_WEI.toString(),
        gasUseEstimate: '350000',
        txFailureReasons: [],
        routeString: `${inputToken.symbol ?? inputToken.address} -> ${outputToken.symbol ?? outputToken.address}`,
        route: [
          [
            {
              type: 'fewv2-pool',
              address: pairAddress,
              tokenIn,
              tokenOut,
              reserve0: { token: reserve0Token, quotient: pair.reserve0.quotient.toString() },
              reserve1: { token: reserve1Token, quotient: pair.reserve1.quotient.toString() },
              amountIn: inputAmount.quotient.toString(),
              amountOut: outputAmount.quotient.toString(),
            },
          ],
        ],
        aggregatedOutputs: [
          {
            token: outputToken.address,
            amount: outputAmount.quotient.toString(),
            minAmount: minimumOutputAmount.quotient.toString(),
            recipient: swapper,
          },
        ],
      },
    }

    return new ClassicTrade({
      quote,
      deadline,
      fewV2Routes: [{ fewRouteV2: route, inputAmount, outputAmount }],
      v2Routes: [],
      v3Routes: [],
      v4Routes: [],
      mixedRoutes: [],
      tradeType,
    })
  } catch {
    return undefined
  }
}
